<template>
  <div class="container-fluid px-4 mt-4">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <div>
        <h5 class="fw-bold mb-2">💳 카드 취소 내역</h5>
        <div class="btn-group btn-group-sm" role="group">
          <router-link to="/admin/payment-result" class="btn btn-outline-primary">결제 내역</router-link>
          <button type="button" class="btn btn-primary active">카드 취소 내역</button>
          <router-link to="/admin/payment-fail-result" class="btn btn-outline-primary">결제 실패 내역</router-link>
        </div>
      </div>
      <span class="text-muted small">총 {{ pagination.total }}건</span>
    </div>

    <!-- 검색 -->
    <div class="row g-2 mb-3">
      <div class="col-auto">
        <div class="input-group">
          <input 
            v-model="searchCarNum" 
            type="text" 
            class="form-control form-control-sm" 
            placeholder="차량번호 입력 (3444 형식)" 
            style="width: 200px;"
            @keyup.enter="performSearch"
          />
          <button class="btn btn-sm btn-primary" @click="performSearch" :disabled="loading">
            {{ loading ? '검색 중...' : '검색' }}
          </button>
          <button class="btn btn-sm btn-outline-secondary" @click="resetSearch">초기화</button>
        </div>
      </div>
      <div class="col-auto">
        <select v-model="currentLimit" class="form-select form-select-sm" style="width: 100px;">
          <option :value="10">10개</option>
          <option :value="20">20개</option>
          <option :value="50">50개</option>
        </select>
      </div>
    </div>

    <!-- 테이블 -->
    <div class="table-responsive">
      <table class="table table-sm table-hover border">
        <thead class="table-light sticky-top">
          <tr>
            <th class="text-center">ID</th>
            <th class="text-center">Tid</th>
            <th class="text-center">차량번호</th>
            <th class="text-center">취소금액</th>
            <th class="text-center">결제수단</th>
            <th class="text-center">취소번호</th>
            <th class="text-center">취소일시</th>
            <th class="text-center">결과코드</th>
            <th class="text-center">결과메시지</th>
            <th class="text-center">주차장</th>
            <th class="text-center">등록일시</th>
            <th class="text-center">작업</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="results.length === 0">
            <td colspan="12" class="text-center text-muted py-4">취소 내역이 없습니다.</td>
          </tr>
          <tr v-for="item in results" :key="item.ID" class="align-middle">
            <td class="text-center small">{{ item.ID }}</td>
            <td class="text-center small">{{ item.Tid || '-' }}</td>
            <td class="text-center fw-bold">{{ item.CAR_NUM || '-' }}</td>
            <td class="text-center text-danger fw-bold">{{ item.CancelAmt ? item.CancelAmt.toLocaleString() : '-' }} 원</td>
            <td class="text-center small">{{ item.PayMethod || '-' }}</td>
            <td class="text-center small">{{ item.CancelNum || '-' }}</td>
            <td class="text-center small">{{ formatCancelDateTime(item.CancelDate, item.CancelTime) }}</td>
            <td class="text-center">
              <span :class="{
                'badge bg-success': item.ResultCode === '0000',
                'badge bg-danger': item.ResultCode && item.ResultCode !== '0000',
                'text-muted': !item.ResultCode
              }">
                {{ item.ResultCode || '-' }}
              </span>
            </td>
            <td class="text-center small" :title="item.ResultMsg">{{ truncate(item.ResultMsg, 15) || '-' }}</td>
            <td class="text-center small">{{ item.AreaName || '-' }}</td>
            <td class="text-center small">{{ formatDate(item.CreatedAt) }}</td>
            <td class="text-center">
              <button 
                class="btn btn-sm btn-outline-info" 
                @click="viewDetails(item)"
                title="상세 정보"
              >
                📋 상세
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 페이징 -->
    <nav v-if="pagination.total > 0" aria-label="Page navigation" class="mt-3">
      <ul class="pagination justify-content-center">
        <li :class="{ 'page-item disabled': currentOffset === 0 }">
          <button class="page-link" @click="previousPage">이전</button>
        </li>
        <li class="page-item active">
          <span class="page-link">{{ currentPage }} / {{ totalPages }}</span>
        </li>
        <li :class="{ 'page-item disabled': !pagination.hasMore }">
          <button class="page-link" @click="nextPage" :disabled="!pagination.hasMore">다음</button>
        </li>
      </ul>
    </nav>

    <!-- 상세 정보 모달 -->
    <div class="modal fade" id="cancelDetailModal" tabindex="-1" aria-labelledby="cancelDetailLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header bg-info text-white">
            <h5 class="modal-title" id="cancelDetailLabel">💳 취소 상세 정보</h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="selectedItem">
              <div class="row mb-3">
                <div class="col-md-6">
                  <p><strong>ID:</strong> {{ selectedItem.ID }}</p>
                  <p><strong>Tid:</strong> <code>{{ selectedItem.Tid || '-' }}</code></p>
                  <p><strong>차량번호:</strong> {{ selectedItem.CAR_NUM || '-' }}</p>
                  <p><strong>취소 금액:</strong> <span class="text-danger fw-bold">{{ selectedItem.CancelAmt ? selectedItem.CancelAmt.toLocaleString() : '-' }} 원</span></p>
                  <p><strong>결제수단:</strong> {{ selectedItem.PayMethod || '-' }}</p>
                </div>
                <div class="col-md-6">
                  <p><strong>취소 번호:</strong> {{ selectedItem.CancelNum || '-' }}</p>
                  <p><strong>취소 일시:</strong> {{ formatCancelDateTime(selectedItem.CancelDate, selectedItem.CancelTime) }}</p>
                  <p><strong>결과코드:</strong> <span :class="{ 'badge bg-success': selectedItem.ResultCode === '0000', 'badge bg-danger': selectedItem.ResultCode !== '0000' }">{{ selectedItem.ResultCode || '-' }}</span></p>
                  <p><strong>결과메시지:</strong> {{ selectedItem.ResultMsg || '-' }}</p>
                </div>
              </div>
              <hr/>
              <div class="row">
                <div class="col-md-6">
                  <p><strong>에러코드:</strong> {{ selectedItem.ErrorCode || '-' }}</p>
                  <p><strong>에러메시지:</strong> {{ selectedItem.ErrorMsg || '-' }}</p>
                  <p><strong>취소 세금:</strong> {{ selectedItem.CancelTaxAmt ? selectedItem.CancelTaxAmt.toLocaleString() : '-' }} 원</p>
                  <p><strong>취소 면세액:</strong> {{ selectedItem.CancelTaxFreeAmt ? selectedItem.CancelTaxFreeAmt.toLocaleString() : '-' }} 원</p>
                </div>
                <div class="col-md-6">
                  <p><strong>남은 금액:</strong> {{ selectedItem.RemainAmt ? selectedItem.RemainAmt.toLocaleString() : '-' }} 원</p>
                  <p><strong>남은 세금:</strong> {{ selectedItem.RemainTaxAmt ? selectedItem.RemainTaxAmt.toLocaleString() : '-' }} 원</p>
                  <p><strong>남은 면세액:</strong> {{ selectedItem.RemainTaxFreeAmt ? selectedItem.RemainTaxFreeAmt.toLocaleString() : '-' }} 원</p>
                  <p><strong>주차장:</strong> {{ selectedItem.AreaName || '-' }}</p>
                </div>
              </div>
              <hr/>
              <p class="text-muted small mb-0"><strong>등록일시:</strong> {{ formatDatetime(selectedItem.CreatedAt) }}</p>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">닫기</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import axios from 'axios'
import * as bootstrap from 'bootstrap'

const SERVER_URL = process.env.VUE_APP_API_BASE

export default {
  setup() {
    const results = ref([])
    const loading = ref(false)
    const searchCarNum = ref('')
    const isSearching = ref(false)
    const currentOffset = ref(0)
    const currentLimit = ref(20)
    const selectedItem = ref(null)
    let cancelDetailModal = null

    const pagination = reactive({
      total: 0,
      limit: 20,
      offset: 0,
      hasMore: false
    })

    const currentPage = computed(() => Math.floor(currentOffset.value / currentLimit.value) + 1)
    const totalPages = computed(() => Math.ceil(pagination.total / currentLimit.value) || 1)

    async function fetchPaymentCancels() {
      loading.value = true
      try {
        const params = {
          limit: currentLimit.value,
          offset: currentOffset.value
        }

        let url = `${SERVER_URL}/api/admin/payment-cancel`
        
        if (isSearching.value && searchCarNum.value) {
          url = `${SERVER_URL}/api/admin/payment-cancel/search`
          params.carNum = searchCarNum.value
        }

        const response = await axios.get(url, { params })
        
        if (response.data.success) {
          results.value = response.data.data || []
          pagination.total = response.data.pagination.total
          pagination.limit = response.data.pagination.limit
          pagination.offset = response.data.pagination.offset
          pagination.hasMore = response.data.pagination.hasMore
        }
      } catch (error) {
        console.error('취소 내역 조회 실패:', error)
        results.value = []
      } finally {
        loading.value = false
      }
    }

    function performSearch() {
      if (searchCarNum.value.trim()) {
        isSearching.value = true
        currentOffset.value = 0
        fetchPaymentCancels()
      }
    }

    function resetSearch() {
      searchCarNum.value = ''
      isSearching.value = false
      currentOffset.value = 0
      fetchPaymentCancels()
    }

    function previousPage() {
      if (currentOffset.value > 0) {
        currentOffset.value -= currentLimit.value
        fetchPaymentCancels()
      }
    }

    function nextPage() {
      if (pagination.hasMore) {
        currentOffset.value += currentLimit.value
        fetchPaymentCancels()
      }
    }

    function viewDetails(item) {
      selectedItem.value = item
      if (!cancelDetailModal) {
        const modalElement = document.getElementById('cancelDetailModal')
        cancelDetailModal = new bootstrap.Modal(modalElement)
      }
      cancelDetailModal.show()
    }

    function formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'Asia/Seoul' })
    }

    function formatDatetime(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })
    }

    function formatCancelDateTime(dateString, timeString) {
      if (!dateString || !timeString) return '-'
      const dateTimeStr = `${dateString}T${timeString}`
      const date = new Date(dateTimeStr)
      return date.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })
    }

    function truncate(str, length) {
      if (!str) return ''
      return str.length > length ? str.substring(0, length) + '...' : str
    }

    onMounted(() => {
      fetchPaymentCancels()
    })

    return {
      results,
      loading,
      searchCarNum,
      currentOffset,
      currentLimit,
      pagination,
      currentPage,
      totalPages,
      selectedItem,
      performSearch,
      resetSearch,
      previousPage,
      nextPage,
      viewDetails,
      formatDate,
      formatDatetime,
      formatCancelDateTime,
      truncate
    }
  }
}
</script>

<style scoped>
.table-responsive {
  border-radius: 8px;
  overflow: hidden;
}

.table {
  margin-bottom: 0;
}

.sticky-top {
  position: sticky;
  top: 0;
  z-index: 10;
}

.badge {
  white-space: nowrap;
}

.modal-header {
  border-bottom: 2px solid rgba(255, 255, 255, 0.2);
}
</style>
